import { ElementRef, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertpopupComponent } from 'src/app/components/alertpopup/alertpopup.component';
import {
  CanvasStringConstants,
  ChatTypes,
  ConversationSettings,
  Session,
  ToolbarOriginType,
} from './canvas.model';
import { TableViewComponent } from '../data-catalog-summary/table-view/table-view.component';
import { CanvasSessionShareDialogComponent } from './canvas-session-share-dialog/canvas-session-share-dialog.component';
import { CanvasDocumentDetailsComponent } from './canvas-citations/canvas-document-details/canvas-document-details.component';
import { cloneDeep } from 'lodash';

@Injectable()
export class CanvasHelperService {
  constructor(private readonly dialog: MatDialog) {}

  /**
   * Gets all text nodes within a given range.
   * @param range - The range to search within.
   * @returns An array of text nodes within the range.
   */
  getTextNodesInRange(range: Range): Text[] {
    const startContainer = range.startContainer;
    const endContainer = range.endContainer;

    // If the range is within a single text node
    if (
      startContainer === endContainer &&
      startContainer.nodeType === Node.TEXT_NODE
    ) {
      return [startContainer as Text];
    }

    // Otherwise, walk through the DOM to find all text nodes
    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const nodeRange = document.createRange();
          nodeRange.selectNodeContents(node);
          return range.intersectsNode(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        },
      }
    );

    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      const textNode = walker.currentNode as Text;
      if (textNode.nodeValue?.trim()) {
        textNodes.push(textNode);
      }
    }
    return textNodes;
  }

  /**
   * Ensures that the block structure of the container is maintained.
   * This function checks if each element is within a valid block structure
   * and moves it to the correct parent if necessary.
   * @param container - The container element to check.
   */
  ensureBlockStructure(container: HTMLElement) {
    const elements = container.querySelectorAll(
      'p, h1, h2, h3, h4, h5, h6, span, strong, em, ul, ol, li, s'
    );
    elements.forEach((element) => {
      const parent = element.parentElement;
      if (parent && !parent.querySelector(element.tagName)) {
        parent.appendChild(element);
      }
    });
  }

  /**
   * Gets the position for a section dialog based on the mouse click event.
   * @param event - The mouse event containing the click position.
   * @returns An object with left and top positions for the dialog.
   */
  getSectionDialogPosition(event: MouseEvent) {
    const clickY = event.clientY;
    const dialogWidth = 340;
    const dialogHeight = 110;
    const margin = 10;

    const screenWidth = window.innerWidth / 2.5 - dialogWidth;
    const screenHeight = window.innerHeight;

    let leftPosition = screenWidth;
    let topPosition = clickY + margin;

    // 2. If button is close to the bottom edge → show dialog above
    if (clickY + dialogHeight + margin > screenHeight) {
      topPosition = clickY - dialogHeight - margin;
    }

    return {
      left: `${leftPosition}px`,
      top: `${topPosition}px`,
      x: leftPosition,
      y: topPosition,
    };
  }

  /**
   * Positions the toolbar based on the current selection.
   * @param selection - The current selection in the document.
   * @param editorRef - Reference to the editor element.
   * @returns An object with x, y coordinates and origin type for the toolbar.
   */
  positionToolbar(
    selection: Selection,
    editorRef: ElementRef<any> | undefined
  ): { x: number; y: number; origin: ToolbarOriginType } {
    const range = selection.getRangeAt(0);
    const selectionRect = range.getBoundingClientRect();
    const containerRect = editorRef?.nativeElement.getBoundingClientRect();

    const toolbarWidth = 300;
    const toolbarHeight = 48;
    const offset = 8;

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;

    // Center the toolbar relative to the container
    let left = (containerRect.left + (toolbarWidth + scrollX)) / 1.5;

    // Default position: below selection
    let top = selectionRect.bottom + scrollY + offset;

    // If there's not enough space below, show above the selection
    if (top + toolbarHeight > scrollY + viewportHeight) {
      top = Math.abs(selectionRect.top) + scrollY - toolbarHeight - offset;
    }

    if (left + toolbarWidth > containerRect.right) {
      left = containerRect.right - toolbarWidth - offset;
    }

    let y = top - offset - 130;
    if (y < 0) {
      y = viewportHeight / 2;
    }

    return {
      x: left - offset - 30,
      y: y,
      origin: ToolbarOriginType.BY_MOUSE_SELECTION,
    };
  }

  /**
   * Gets the HTML content of the user's highlights.
   * This function retrieves the selected text and wraps it in appropriate tags.
   * It also ensures that block elements are preserved correctly.
   * @returns The HTML string of the user's highlights.
   */
  getUserHiglights() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return '';

    const range = selection.getRangeAt(0);
    const container = document.createElement('div');
    container.appendChild(range.cloneContents());

    let contentHtml = container.innerHTML;

    // List of block tags we want to preserve if they're part of selection
    const BLOCK_TAGS = ['LI', 'P', 'H1', 'H2', 'H3'];

    // Check and wrap <table> if needed
    const includesThead = contentHtml.includes('<thead');
    const includesTbody = contentHtml.includes('<tbody');
    const includesTable = contentHtml.includes('<table');
    if ((includesThead || includesTbody) && !includesTable) {
      contentHtml = `<table>${contentHtml}</table>`;
    }

    // If selection includes a single block tag and it's not wrapped properly
    const selectedNodes: any = Array.from(container.childNodes);
    if (
      selectedNodes.length === 1 &&
      selectedNodes[0].nodeType === Node.ELEMENT_NODE &&
      BLOCK_TAGS.includes((selectedNodes[0] as HTMLElement).tagName)
    ) {
      const tag = (selectedNodes[0] as HTMLElement).tagName.toLowerCase();
      contentHtml = `<${tag}>${selectedNodes[0].innerHTML}</${tag}>`;
    }

    return contentHtml;
  }

  /**
   * Formats the statistics tooltip for a given stat object.
   * @param stat - The stat object containing usage cost and total time.
   * @returns A formatted string for the tooltip.
   */
  getStatsTooltip(stat: any) {
    try {
      const formatTime = (time: number) => {
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time % 3600) / 60);
        const seconds = Number((time % 60).toFixed(2));
        const parts = [];
        if (hours > 0) parts.push(`${hours}hour${hours > 1 ? 's' : ''}`);
        if (minutes > 0) parts.push(`${minutes}min${minutes > 1 ? 's' : ''}`);
        if (seconds > 0) parts.push(`${seconds}sec`);
        return parts.join(' ');
      };

      return `Cost: $${stat?.usage_cost}, Duration: ${formatTime(
        Math.floor(stat?.total_time)
      )}.`;
    } catch (error) {
      console.error(error);
      return `Cost: $${stat?.usage_cost}, Duration: ${stat?.total_time} seconds.`;
    }
  }

  /**
   * Handles the application of a class to a text node.
   * This function checks the class name and applies the appropriate formatting.
   * @param className - The class name to apply.
   * @param textNode - The text node to format.
   * @param parent - The parent element of the text node.
   */
  public handleClassApplication(
    className: string,
    textNode: Node,
    parent: HTMLElement
  ): void {
    if (className === 'ft-bold') {
      this.toggleBold(textNode, parent);
    } else if (className === 'ft-italic') {
      this.toggleItalic(textNode, parent);
    } else if (className === 'ft-underline') {
      this.toggleUnderline(textNode, parent);
    } else if (className === 'ft-strike') {
      this.toggleStrikethrough(textNode, parent);
    } else {
      this.toggleCustomClass(className, textNode, parent);
    }
  }

  /**
   * Toggles the bold formatting for a text node.
   * If the parent is already bold, it unwraps it; otherwise, it wraps the text in a <strong> tag.
   * @param textNode - The text node to format.
   * @param parent - The parent element of the text node.
   */
  private toggleBold(textNode: Node, parent: HTMLElement): void {
    if (parent.tagName === 'STRONG') {
      this.unwrapElement(parent);
    } else {
      this.wrapElement('strong', textNode, parent);
    }
  }

  /**
   * Toggles the italic formatting for a text node.
   * If the parent is already italic, it unwraps it; otherwise, it wraps the text in an <em> tag.
   * @param textNode - The text node to format.
   * @param parent - The parent element of the text node.
   */
  private toggleItalic(textNode: Node, parent: HTMLElement): void {
    if (parent.tagName === 'EM') {
      this.unwrapElement(parent);
    } else {
      this.wrapElement('em', textNode, parent);
    }
  }

  /**
   * Toggles the underline formatting for a text node.
   * If the parent is already underlined, it unwraps it; otherwise, it wraps the text in a <u> tag.
   * @param textNode - The text node to format.
   * @param parent - The parent element of the text node.
   */
  private toggleUnderline(textNode: Node, parent: HTMLElement): void {
    if (parent.tagName === 'U') {
      this.unwrapElement(parent);
    } else {
      this.wrapElement('u', textNode, parent);
    }
  }

  /**
   * Toggles the strikethrough formatting for a text node.
   * If the parent is already strikethrough, it unwraps it; otherwise, it wraps the text in a <s> tag.
   * @param textNode - The text node to format.
   * @param parent - The parent element of the text node.
   */
  private toggleStrikethrough(textNode: Node, parent: HTMLElement): void {
    if (parent.tagName === 'S') {
      this.unwrapElement(parent);
    } else {
      this.wrapElement('s', textNode, parent);
    }
  }

  /**
   * Toggles a custom class on a text node.
   * If the parent already has the class, it removes
   * it; otherwise, it wraps the text in a <span> with the class.
   * @param className - The class name to toggle.
   * @param textNode - The text node to format.
   *  @param parent - The parent element of the text node.
   */
  public toggleCustomClass(
    className: string,
    textNode: Node,
    parent: HTMLElement
  ): void {
    const hasClass = parent.classList.contains(className);
    if (hasClass) {
      parent.classList.remove(className);
    } else {
      const span = document.createElement('span');
      span.classList.add(className);
      span.textContent = textNode.textContent;
      parent.replaceChild(span, textNode);
    }
  }

  /**
   * Wraps a text node in a specified HTML tag.
   * If the tag is already present in the parent hierarchy, it unwraps it.
   * @param tagName - The name of the tag to wrap the text node in.
   * @param textNode - The text node to wrap.
   * @param parent - The parent element of the text node.
   */
  private wrapElement(
    tagName: string,
    textNode: Node,
    parent: HTMLElement
  ): void {
    // Traverse through the parent hierarchy to check for the tag
    let currentParent: HTMLElement | null = parent;
    let tagFound = false;

    while (currentParent && !this.isValidTag(currentParent.tagName)) {
      if (currentParent.tagName.toLowerCase() === tagName.toLowerCase()) {
        tagFound = true;
        break;
      }
      currentParent = currentParent.parentElement;
    }

    if (tagFound) {
      // If the tag is found, unwrap it
      this.unwrapElement(currentParent as HTMLElement);
    } else {
      // If the tag is not found, add it
      const element = document.createElement(tagName);

      // Move the textNode into the new element
      element.appendChild(textNode.cloneNode(true));

      // Replace the textNode with the new element
      parent.replaceChild(element, textNode);
    }
  }

  /**
   * Unwraps an HTML element by moving its children to its parent and removing the element itself.
   * @param element - The HTML element to unwrap.
   */
  private unwrapElement(element: HTMLElement): void {
    const grandParent = element.parentElement;

    // Move all children of the element to its grandparent
    while (element.firstChild) {
      grandParent?.insertBefore(element.firstChild, element);
    }

    // Remove the element itself
    grandParent?.removeChild(element);
  }

  /**
   * Checks if the given tag name is valid for traversal.
   * @param tagName - The tag name to check.
   * @returns True if the tag is valid, false otherwise.
   */
  private isValidTag(tagName: string): boolean {
    const validTags = [
      'DIV',
      'P',
      'H1',
      'H2',
      'H3',
      'H4',
      'H5',
      'H6',
      'SPAN',
      'UL',
      'OL',
      'LI',
    ];
    return validTags.includes(tagName.toUpperCase());
  }

  /**
   * Checks if the given element is a block-level element.
   * @param element - The HTML element to check.
   * @returns True if the element is a block-level element, false otherwise.
   */
  isWithinBlockElement(element: HTMLElement): boolean {
    const blockElements = [
      'P',
      'H1',
      'H2',
      'H3',
      'H4',
      'H5',
      'H6',
      'SPAN',
      'STRONG',
      'EM',
      'S',
      'UL',
      'OL',
      'LI',
    ];
    return blockElements.includes(element.nodeName.toUpperCase());
  }

  checkIfConversationAllowed(
    conversationSettings: ConversationSettings,
    chatMessages: any = []
  ): boolean {
    let conversations = cloneDeep(chatMessages).filter(
      (x: any) => x.type === ChatTypes.CHAT || x.type == ChatTypes.CANVAS_CHAT
    );

    if (conversations.length >= conversationSettings.conversation_max_limit) {
      this.showTresholdReachedDialog(conversationSettings);
      return false;
    }

    if (
      conversations.length >= conversationSettings.conversation_warning_limit
    ) {
      this.showTresholdWarningDialog(conversationSettings);
    }

    return true;
  }
  checkIfSharingAllowed(
    sharedCount: number,
    conversationSettings: ConversationSettings
  ) {
    if (sharedCount > conversationSettings.session_share_max_limit) {
      this.showSharedReachedDialog(conversationSettings);
    }
  }
  showSharedReachedDialog(conversationSettings: ConversationSettings) {
    this.showConfirmationDialogWithCustomButtons(
      CanvasStringConstants.SHARED_LIMIT_REACHED_TITLE +
        ` (${conversationSettings.session_share_max_limit})`,
      CanvasStringConstants.SHARED_LIMIT_REACHED_MESSAGE,
      undefined,
      CanvasStringConstants.CONVERSATION_LIMIT_CANCEL_BUTTON
    );
  }
  showTresholdWarningDialog(conversationSettings: ConversationSettings) {
    this.showConfirmationDialogWithCustomButtons(
      CanvasStringConstants.CONVERSATION_LIMIT_WARNING_TITLE,
      `${
        CanvasStringConstants.CONVERSATION_LIMIT_WARNING_MESSAGE
      } Maximum conversations allowed per session is "${
        conversationSettings.conversation_max_limit
      }" and you have "${
        conversationSettings.conversation_max_limit -
        conversationSettings.conversation_warning_limit
      }" conversations left in your quota.`,
      CanvasStringConstants.CONVERSATION_LIMIT_WARNING_BUTTON,
      CanvasStringConstants.CONVERSATION_LIMIT_CANCEL_BUTTON
    );
  }

  showTresholdReachedDialog(conversationSettings: ConversationSettings) {
    this.showConfirmationDialogWithCustomButtons(
      CanvasStringConstants.CONVERSATION_LIMIT_REACHED_TITLE +
        ` (${conversationSettings.conversation_max_limit})`,
      CanvasStringConstants.CONVERSATION_LIMIT_REACHED_MESSAGE,
      undefined,
      CanvasStringConstants.CONVERSATION_LIMIT_CANCEL_BUTTON
    );
  }

  showFailedToLoadSessionsDialog() {
    this.showConfirmationDialogWithCustomButtons(
      CanvasStringConstants.FAILED_TO_LOAD_SESSIONS_TITLE,
      CanvasStringConstants.FAILED_TO_LOAD_SESSIONS_MESSAGE,
      undefined,
      CanvasStringConstants.FAILED_TO_LOAD_SESSIONS_BUTTON
    );
  }

  showConfirmationDialog() {
    return this.showConfirmationDialogWithCustomButtons(
      CanvasStringConstants.DIRTY_CANVAS_SAVE_TILE,
      CanvasStringConstants.DIRTY_CANVAS_SAVE_MESSAGE,
      CanvasStringConstants.DIRTY_CANVAS_SAVE_BUTTON,
      CanvasStringConstants.DIRTY_CANVAS_CANCEL_BUTTON
    );
  }

  showConfirmationDiscardDialog() {
    return this.showConfirmationDialogWithCustomButtons(
      CanvasStringConstants.DIRTY_CANVAS_SAVE_TILE,
      CanvasStringConstants.DIRTY_CANVAS_DISCARD_MESSAGE,
      CanvasStringConstants.DIRTY_CANVAS_DISCARD_CONTINUE_BUTTON,
      CanvasStringConstants.DIRTY_CANVAS_DISCARD_CANCEL_BUTTON
    );
  }

  showConfirmationShareRemoveDialog() {
    return this.showConfirmationDialogWithCustomButtons(
      CanvasStringConstants.REMOVE_SHARE_USER_TITLE,
      CanvasStringConstants.REMOVE_SHARE_USER_MESSAGE,
      CanvasStringConstants.REMOVE_SHARE_USER_PROCEED_BUTTON,
      CanvasStringConstants.REMOVE_SHARE_USER_CANCEL_BUTTON
    );
  }

  /**
   * Shows a confirmation dialog with custom buttons.
   * @param title - The title of the dialog.
   * @param message - The message to display in the dialog.
   * @param positiveButtonMessage - The text for the positive button.
   * @param negativeButtonMessage - The text for the negative button.
   * @returns A promise that resolves if the user confirms, or rejects if they cancel.
   */
  private showConfirmationDialogWithCustomButtons(
    title: string,
    message: string,
    positiveButtonMessage: string | undefined,
    negativeButtonMessage: string
  ) {
    return new Promise((resolve, reject) => {
      let dialogRef = this.dialog.open(AlertpopupComponent, {
        hasBackdrop: true,
        disableClose: true,
        autoFocus: false,
        width: '50vw',
        backdropClass: 'backdropBackground',
        data: {
          title,
          message,
          postitionButtonMessage: positiveButtonMessage,
          negativeButtonMessage,
        },
      });
      dialogRef.afterClosed().subscribe((result: any) => {
        if (result) {
          resolve(true);
          return;
        }
        reject(new Error('User cancelled'));
      });
    });
  }

  /**
   * Opens the dataset details dialog.
   * @param dataset_id - The ID of the dataset to view details for.
   */
  openDatasetDetails(dataset_id: any) {
    let dialogref = this.dialog.open(TableViewComponent, {
      disableClose: false,
      backdropClass: 'backdropBackground',
      data: { id: dataset_id },
    });
    dialogref.afterClosed().subscribe((result: any) => {});
  }

  /**
   * Opens the session share dialog.
   * @param session - The session to share.
   */
  openSessionShareDialog(session: Session) {
    this.dialog.open(CanvasSessionShareDialogComponent, {
      width: '75vw',
      height: '550vh',
      maxHeight: '55vh',
      disableClose: true,
      hasBackdrop: true,
      data: { session },
    });
  }

  /**
   * Opens the document citation details dialog.
   * @param source - The source document to view details for.
   */
  openDocumentCitationDetails(source: any) {
    this.dialog.open(CanvasDocumentDetailsComponent, {
      width: '65vw',
      height: '90vh',
      disableClose: false,
      hasBackdrop: true,
      data: { sourceDocument: source },
    });
  }
}
